import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { TOTAL_PAGES } from '../utils/quranData';
import { Profile } from '../types';
import { profileService } from '../services/dataService';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';

function Dashboard(): React.ReactElement {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async (): Promise<void> => {
    try {
      const response = await profileService.getAll();
      if (response.data.success) {
        setProfiles(response.data.profiles || []);
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = (pages: number): number => {
    return Math.round((pages / TOTAL_PAGES) * 100);
  };

  if (loading) {
    return (
      <div className="flex align-items-center justify-content-center" style={{ height: '50vh' }}>
        <i className="pi pi-spin pi-spinner text-4xl"></i>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-2xl font-bold mb-1">
          {t('dashboard.welcome')}, {user?.name || 'User'}
        </h1>
        <p className="text-color-secondary m-0">{t('app.tagline')}</p>
      </div>

      {profiles.length === 0 ? (
        <Card className="text-center p-5">
          <i className="pi pi-user-plus text-5xl text-color-secondary mb-3"></i>
          <h3>{t('profile.create')}</h3>
          <p className="text-color-secondary mb-4">{t('app.tagline')}</p>
          <Button 
            label={t('profile.create')} 
            icon="pi pi-plus"
            onClick={() => navigate('/profiles')}
          />
        </Card>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="stats-grid mb-4">
            <div className="stat-card">
              <div className="stat-value">{profiles[0]?.memorized_pages || 0}</div>
              <div className="stat-label">{t('dashboard.pagesMemorized')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#f59e0b' }}>
                {profiles[0]?.needs_review || 0}
              </div>
              <div className="stat-label">{t('dashboard.needsReview')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{calculateProgress(profiles[0]?.memorized_pages || 0)}%</div>
              <div className="stat-label">{t('dashboard.progress')}</div>
            </div>
          </div>

          {/* Progress Overview */}
          <Card className="mb-4">
            <h3 className="mt-0 mb-3">{t('dashboard.progress')}</h3>
            <div className="flex align-items-center gap-3 mb-3">
              <div className="progress-ring" style={{ '--progress': `${calculateProgress(profiles[0]?.memorized_pages || 0) * 3.6}deg` } as React.CSSProperties}>
                <div className="progress-ring-inner">
                  {calculateProgress(profiles[0]?.memorized_pages || 0)}%
                </div>
              </div>
              <div>
                <div className="text-xl font-bold">
                  {profiles[0]?.memorized_pages || 0} / {TOTAL_PAGES}
                </div>
                <div className="text-color-secondary">{t('dashboard.totalPages')}</div>
              </div>
            </div>
            <ProgressBar 
              value={calculateProgress(profiles[0]?.memorized_pages || 0)} 
              showValue={false}
              style={{ height: '8px' }}
            />
          </Card>

          {/* Quick Actions */}
          <Card className="mb-4">
            <h3 className="mt-0 mb-3">{t('dashboard.quickActions')}</h3>
            <div className="flex flex-wrap gap-2">
              <Button 
                label={t('dashboard.addPage')} 
                icon="pi pi-plus"
                onClick={() => navigate(`/pages/${profiles[0]?.id}`)}
              />
              <Button 
                label={t('dashboard.reviewDue')} 
                icon="pi pi-refresh"
                className="p-button-warning"
                onClick={() => navigate(`/pages/${profiles[0]?.id}?filter=review`)}
              />
            </div>
          </Card>

          {/* Profiles List */}
          <Card>
            <div className="flex justify-content-between align-items-center mb-3">
              <h3 className="m-0">{t('nav.profiles')}</h3>
              <Button 
                icon="pi pi-plus" 
                className="p-button-rounded p-button-text"
                onClick={() => navigate('/profiles')}
              />
            </div>
            {profiles.map(profile => (
              <div 
                key={profile.id}
                className="flex justify-content-between align-items-center p-3 border-round hover:surface-hover cursor-pointer"
                onClick={() => navigate(`/pages/${profile.id}`)}
              >
                <div>
                  <div className="font-medium">{profile.name}</div>
                  <div className="text-sm text-color-secondary">
                    {profile.memorized_pages} {t('dashboard.pagesMemorized')}
                  </div>
                </div>
                <i className="pi pi-chevron-right text-color-secondary"></i>
              </div>
            ))}
          </Card>
        </>
      )}
    </div>
  );
}

export default Dashboard;
